import React, { useMemo } from 'react';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import { ItemPayment, SettingsProps } from '@pages/types';
import PaymentMethodList from '@components/Method/PaymentMethodList';

interface CardPaymentMethodProps {
  dataImg: ItemPayment[];
  name: string;
  type: string;
  icon?: React.ReactNode;
  isOpen?: boolean;
  onToggle?: () => void;
  settings: SettingsProps;
  onPaymentSelected?: () => void;
  selectedPaymentType?: ItemPayment;
  setSelectedPaymentType?: (value: ItemPayment | undefined) => void;
}

const SUPPORTED_PAYMENT_TYPES = {
  VIRTUAL_ACCOUNT: PaymentMethodList,
  'E-WALLET': PaymentMethodList,
  QRIS: PaymentMethodList,
  CARD: PaymentMethodList,
  DIRECT_DEBIT: PaymentMethodList,
} as const;

const CardPaymentMethod: React.FC<CardPaymentMethodProps> = ({
  dataImg,
  name,
  type,
  icon,
  isOpen = false,
  onToggle,
  settings,
  selectedPaymentType,
  setSelectedPaymentType,
}) => {
  const MethodComponent = useMemo(
    () => SUPPORTED_PAYMENT_TYPES[type as keyof typeof SUPPORTED_PAYMENT_TYPES],
    [type]
  );

  if (!dataImg?.length) {
    return null;
  }

  if (!MethodComponent) {
    console.warn(`Payment type "${type}" is not supported yet`);
    return null;
  }

  return (
    <div>
      <div
        className="flex cursor-pointer select-none flex-row items-center justify-between rounded-lg border border-gray-300 px-3.5 py-3 transition-colors hover:bg-gray-50"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle?.();
          }
        }}
      >
        <div className="flex flex-row items-center gap-3">
          {icon}
          <span className="text-base font-semibold text-gray-800">{name}</span>
        </div>

        <div className="flex flex-row items-center gap-2">
          {isOpen ? (
            <IconChevronUp className="h-6 w-6 text-gray-800" />
          ) : (
            <IconChevronDown className="h-6 w-6 text-gray-800" />
          )}
        </div>
      </div>

      {isOpen && (
        <div className="select-none">
          <MethodComponent
            data={dataImg}
            settings={settings}
            selectedPaymentType={selectedPaymentType}
            setSelectedPaymentType={setSelectedPaymentType}
          />
        </div>
      )}
    </div>
  );
};

export default CardPaymentMethod;
